import { z } from 'zod';
import { categoryIdSchema, i18nTextSchema } from '../platform.js';
import { fileNameSchema } from '../manifest.js';
import { APP_ID_PATTERN_SOURCE } from '../slug.js';
import { releaseViewSchema, appSummarySchema } from './catalog.js';

export const i18nInputSchema = z.record(z.enum(['en', 'ru']), z.string());

export const createAppSchema = z.object({
  /** Fully qualified app id; the app slug must match its own final segment. */
  id: z
    .string()
    .min(3)
    .max(128)
    .regex(new RegExp(APP_ID_PATTERN_SOURCE), 'appId must be "developer-slug/app-slug"'),
  name: z.string().min(1).max(120),
  category: categoryIdSchema,
  summary: i18nInputSchema,
  description: i18nInputSchema,
  homepageUrl: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'homepage must use https')
    .optional(),
  license: z.string().max(64).optional(),
});
export type CreateAppInput = z.infer<typeof createAppSchema>;

export const updateAppSchema = createAppSchema
  .omit({ id: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'at least one field required' });
export type UpdateAppInput = z.infer<typeof updateAppSchema>;

/**
 * Package file name. Kept for manifests and for the publisher URL path, which
 * is validated but never used to build a local path.
 */
export const releaseFileSchema = fileNameSchema;

const sha256Schema = z
  .string()
  .regex(/^[a-f0-9]{64}$/i, 'sha256 must be 64 hex characters')
  .transform((v) => v.toLowerCase());

/**
 * A release points at the publisher's own installer.
 *
 * Appwin stores metadata only: the file is downloaded from `downloadUrl` and
 * verified against `sha256`. The hash is mandatory — without it the client
 * cannot prove that the bytes it received are the bytes that were reviewed.
 */
export const submitReleaseSchema = z
  .object({
    version: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[0-9A-Za-z][0-9A-Za-z.+-]*$/),
    platform: z.literal('windows'),
    arch: z.array(z.enum(['x86', 'x64', 'arm64', 'universal'])).min(1),
    kind: z.enum(['msix', 'appx', 'msi', 'exe', 'zip']),
    scope: z.enum(['per_user', 'machine', 'portable']),
    /** Publisher-hosted installer. https only. */
    downloadUrl: z
      .string()
      .url()
      .max(2048)
      .refine((value) => value.startsWith('https://'), 'download url must use https'),
    sha256: sha256Schema,
    /** Declared size, checked against the response when the link is verified. */
    sizeBytes: z.number().int().positive().optional(),
    silentArgs: z.array(z.string().min(1)).optional(),
    upgradeArgs: z.array(z.string().min(1)).optional(),
    minOs: z
      .string()
      .regex(/^\d+\.\d+\.\d+$/)
      .optional(),
    changelog: z.record(z.string().min(2).max(5), z.string()).optional(),
  })
  .superRefine((value, ctx) => {
    if ((value.kind === 'exe' || value.kind === 'msi') && (value.silentArgs?.length ?? 0) === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['silentArgs'],
        message: 'exe/msi releases require silent install arguments for automatic updates',
      });
    }
  });
export type SubmitReleaseInput = z.infer<typeof submitReleaseSchema>;

export const developerAppSchema = appSummarySchema.extend({
  status: z.enum(['draft', 'in_review', 'published', 'delisted']),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
  releaseCount: z.number().int().nonnegative(),
});
export type DeveloperApp = z.infer<typeof developerAppSchema>;

export const developerAppsResponseSchema = z.object({
  items: z.array(developerAppSchema),
});
export type DeveloperAppsResponse = z.infer<typeof developerAppsResponseSchema>;

export const developerAppDetailSchema = developerAppSchema.extend({
  description: i18nTextSchema,
  homepageUrl: z.string().nullable(),
  license: z.string().nullable(),
  releases: z.array(releaseViewSchema),
});
export type DeveloperAppDetail = z.infer<typeof developerAppDetailSchema>;

export const developerStatsSchema = z.object({
  apps: z.number().int().nonnegative(),
  releasesPublished: z.number().int().nonnegative(),
  releasesInReview: z.number().int().nonnegative(),
  installs7d: z.number().int().nonnegative(),
  updates7d: z.number().int().nonnegative(),
  /** Null when telemetry is unavailable, so the UI shows "no data", not 0%. */
  successRate7d: z.number().min(0).max(1).nullable(),
});
export type DeveloperStats = z.infer<typeof developerStatsSchema>;

export const createApiKeySchema = z.object({
  label: z.string().max(64).optional(),
  scope: z.enum(['read', 'publish']),
});
export type CreateApiKeyInput = z.infer<typeof createApiKeySchema>;

export const apiKeyCreatedSchema = z.object({
  id: z.string(),
  /** Returned exactly once; never retrievable afterwards. */
  token: z.string(),
  label: z.string().nullable(),
  scope: z.enum(['read', 'publish']),
  createdAt: z.number().int(),
});
export type ApiKeyCreated = z.infer<typeof apiKeyCreatedSchema>;

export const apiKeyViewSchema = z.object({
  id: z.string(),
  label: z.string().nullable(),
  scope: z.enum(['read', 'publish']),
  createdAt: z.number().int(),
  revokedAt: z.number().int().nullable(),
});
export type ApiKeyView = z.infer<typeof apiKeyViewSchema>;

export const claimAppSchema = z.object({
  /** winget PackageIdentifier to claim. */
  packageIdentifier: z.string().min(3).max(128),
  /** Evidence: a URL on the publisher domain or a signature subject. */
  evidence: z.string().min(3).max(512),
});
export type ClaimAppInput = z.infer<typeof claimAppSchema>;

export const emailCodeStartSchema = z.object({ email: z.string().email() });
export type EmailCodeStartInput = z.infer<typeof emailCodeStartSchema>;

export const emailCodeVerifySchema = z.object({
  email: z.string().email(),
  code: z.string().regex(/^\d{6}$/),
});
export type EmailCodeVerifyInput = z.infer<typeof emailCodeVerifySchema>;

export const sessionViewSchema = z.object({
  developerId: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: z.enum(['dev', 'admin']),
  expiresAt: z.number().int(),
});
export type SessionView = z.infer<typeof sessionViewSchema>;
